import { useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";
import {
  LeftToRightListBulletIcon,
  Mail02Icon,
  Time04Icon,
  UserCircleIcon,
  WebhookIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { PreviewFrame } from "./PreviewFrame";

// The teaser is laid out on the Figma canvas (550×244) and scaled to the
// frame's real width, so the tilted settings keep the export's proportions.
const CANVAS_WIDTH = 550;
const CANVAS_HEIGHT = 244;

const NODE_WIDTH = 220;
const NODE_HEIGHT = 60;
const TILT = (15 * Math.PI) / 180;
// Figma's left/top are the rotated node's bounding box, so each node is placed
// by that box's centre and rotated about its own.
const OFFSET_X =
  (NODE_WIDTH * Math.cos(TILT) + NODE_HEIGHT * Math.sin(TILT) - NODE_WIDTH) / 2;
const OFFSET_Y =
  (NODE_WIDTH * Math.sin(TILT) + NODE_HEIGHT * Math.cos(TILT) - NODE_HEIGHT) /
  2;

const SHADOW_MD =
  "0px 4px 8px -2px rgba(28,29,34,0.1), 0px 2px 4px -2px rgba(28,29,34,0.06)";
const SHADOW_HARD = "0px 4px 4px rgba(0,0,0,0.25)";

type Node = {
  icon: IconSvgElement;
  iconClassName?: string;
  label: string;
  value: string;
  left: number;
  top: number;
  shadow: string;
};

const NODES: Node[] = [
  {
    icon: WebhookIcon,
    iconClassName: "text-primary-500",
    label: "Message",
    value: "Send SMS",
    left: -73,
    top: 120.03,
    shadow: SHADOW_HARD
  },
  {
    icon: Mail02Icon,
    label: "Message",
    value: "Send email",
    left: 147.88,
    top: 59.53,
    shadow: SHADOW_MD
  },
  {
    icon: Time04Icon,
    label: "Timing",
    value: "Every 14 days",
    left: 372.05,
    top: -1.38,
    shadow: SHADOW_HARD
  },
  {
    icon: UserCircleIcon,
    label: "Audience",
    value: "Tenant leads",
    left: 43.84,
    top: 158.08,
    shadow: SHADOW_MD
  },
  {
    icon: LeftToRightListBulletIcon,
    label: "Sequence",
    value: "Day 0 · 3 · 7",
    left: 265.04,
    top: 98.81,
    shadow: SHADOW_MD
  }
];

const SettingNode = ({ node }: { node: Node }) => (
  <div
    className="absolute box-border flex -rotate-[15deg] items-center gap-2 rounded-lg border border-alpha-100 bg-white p-3"
    style={{
      left: node.left + OFFSET_X,
      top: node.top + OFFSET_Y,
      width: NODE_WIDTH,
      height: NODE_HEIGHT,
      boxShadow: node.shadow
    }}
  >
    <span className="flex size-9 shrink-0 items-center justify-center rounded-[7px] border border-alpha-100">
      <HugeiconsIcon
        icon={node.icon}
        size={24}
        className={node.iconClassName ?? "text-content-1000"}
      />
    </span>
    <span className="min-w-0">
      <span className="block text-[13px] font-medium leading-4 text-content-300">
        {node.label}
      </span>
      <span className="block whitespace-nowrap text-sm font-medium leading-5 text-content-1000">
        {node.value}
      </span>
    </span>
  </div>
);

const EDGE_FADE: CSSProperties = {
  backgroundImage:
    "linear-gradient(90deg, rgba(231,1,127,0) 17.57%, #e7017f 100%)",
  backdropFilter: "blur(2px)"
};

const useCanvasScale = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const frame = ref.current?.parentElement;
    if (!frame) return undefined;
    const update = () => setScale(frame.clientWidth / CANVAS_WIDTH);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(frame);
    return () => observer.disconnect();
  }, []);

  return { ref, scale };
};

export const CommunicationCampaignsPreview = () => {
  const { ref, scale } = useCanvasScale();

  return (
    <PreviewFrame
      noInnerCard
      badge="pro"
      centerBadge
      aspectRatio={`${CANVAS_WIDTH} / ${CANVAS_HEIGHT}`}
    >
      <div
        ref={ref}
        className="absolute left-0 top-0 origin-top-left overflow-hidden"
        style={{
          width: CANVAS_WIDTH,
          height: CANVAS_HEIGHT,
          transform: `scale(${scale})`
        }}
        aria-hidden="true"
      >
        {NODES.map((node) => (
          <SettingNode key={node.value} node={node} />
        ))}
        <div
          className="pointer-events-none absolute right-0 top-0 h-full w-[117px] opacity-[0.12]"
          style={EDGE_FADE}
        />
        <div
          className="pointer-events-none absolute left-0 top-0 h-full w-[146px] -scale-x-100 opacity-[0.05]"
          style={EDGE_FADE}
        />
      </div>
    </PreviewFrame>
  );
};
